import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ControlOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getConnectorDisplayName,
  getConnectorId,
} from "@p0security/shared/types/ai-inventory";
import {
  ApprovalRule,
  ResourceRule,
  RoutingRule,
} from "@p0security/shared/types/workflow/types";
import { nonNullable } from "@p0security/shared/util/nonNullable";
import {
  Button,
  Flex,
  Input,
  Modal,
  Progress,
  Segmented,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { useEnumSearchParam } from "hooks/useEnumSearchParam";
import { usePagination } from "hooks/usePagination";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { colors } from "styles/variables";
import { stopPropagation } from "utils/javascript";

import { useRoutingRulesStore } from "../../../../store/routingRulesStore";
import { useInventoryData } from "../../../Assessment/components/AiInventory/hooks/useInventoryData";
import { isConnectorManaged } from "../../../Assessment/components/AiInventory/platforms/enrollment";
import { SelectedEnvironmentContext } from "../../../Environment/contexts/SelectedEnvironmentContext";
import {
  ResizableTable,
  type ResizableTableColumnsType,
} from "../../../ResizableTable.tsx";
import { NamePreview } from "../components/previews/NamePreview";
import { RequestorPreview } from "../components/previews/RequestorPreview";
import { ResourcePreview } from "../components/previews/ResourcePreview";
import { GovernanceDetailDrawer } from "../governance/GovernanceDetailDrawer";
import {
  GOVERNANCE_POLICIES,
  GovernanceViolator,
  PolicyRow,
  getGovernancePolicyStats,
  isControlRow,
  isGovernanceRow,
} from "../governance/governancePolicies";
import { useSubmitWorkflow } from "../useSubmitWorkflow";
import { getResourceFilters, getUniqueResources, hashRule } from "../utils";
import { ApprovalSummary } from "./common";

const StyledSearchContainer = styled.div`
  padding: 8px;
`;

const StyledSearchButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTableContainer = styled.div<{ $canSubmit: boolean }>`
  .disabled-rule {
    background: #fed;
  }

  .ant-table-tbody > tr.control-row {
    cursor: ${({ $canSubmit }) => ($canSubmit ? "pointer" : "default")};
  }

  .ant-table-tbody > tr.governance-row {
    cursor: pointer;
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr > td:not(:first-child) {
    div,
    span,
    p {
      font-weight: normal;
    }
  }
`;

const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: ${colors.neutral["05"]};
  border: 1px solid ${colors.neutral["15"]};
  border-radius: 8px;
`;

type TypeFilter = "control" | "governance";
type StatusFilter = "all" | "disabled" | "enabled";

const TYPE_FILTER_DEFAULT: TypeFilter = "control";
const STATUS_FILTER_DEFAULT: StatusFilter = "enabled";

const isTypeFilter = (v: unknown): v is TypeFilter =>
  v === "control" || v === "governance";
const isStatusFilter = (v: unknown): v is StatusFilter =>
  v === "all" || v === "enabled" || v === "disabled";

const segmentedLabel = (icon: ReactNode, label: string) => (
  <Space size={6}>
    {icon}
    {label}
  </Space>
);

const TYPE_FILTER_OPTIONS = [
  { label: segmentedLabel(<ControlOutlined />, "Control"), value: "control" },
  {
    label: segmentedLabel(<SafetyOutlined />, "Governance"),
    value: "governance",
  },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  {
    label: segmentedLabel(<CheckCircleOutlined />, "Enabled"),
    value: "enabled",
  },
  {
    label: segmentedLabel(<CloseCircleOutlined />, "Disabled"),
    value: "disabled",
  },
];

/** Prevents click events from bubbling to the row's onClick handler */
const CellAction: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div onClick={stopPropagation}>{children}</div>
);

const { confirm } = Modal;

type Props = {
  canSubmit: boolean;
  handleDelete: (record: RoutingRule) => void;
};

const FilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  placeholder,
}: any) => {
  const switchFilter = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setSelectedKeys(e.target.value ? [e.target.value] : []),
    [setSelectedKeys]
  );
  const resetSearch = useCallback(() => {
    setSelectedKeys([]);
    confirm();
  }, [confirm, setSelectedKeys]);

  return (
    <StyledSearchContainer>
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={switchFilter}
        onPressEnter={confirm}
      />
      <StyledSearchButtonContainer>
        <Button
          onClick={resetSearch}
          type="link"
          size="small"
          disabled={!selectedKeys[0]}
        >
          Reset
        </Button>
        <Button type="primary" onClick={confirm} size="small">
          Search
        </Button>
      </StyledSearchButtonContainer>
    </StyledSearchContainer>
  );
};

const ComplianceCell: React.FC<{ percent: number }> = ({ percent }) => {
  const tone =
    percent >= 90
      ? colors.success["50"]
      : percent >= 60
        ? colors.themeColors.lightOrange
        : colors.themeColors.red;

  return (
    <Space style={{ minWidth: 140 }}>
      <Progress
        percent={percent}
        showInfo={false}
        strokeColor={tone}
        size="small"
        style={{ width: 110, marginBottom: 0 }}
      />
      <Typography.Text type="secondary" code>
        {percent}%
      </Typography.Text>
    </Space>
  );
};

const TableView: React.FC<Props> = ({ canSubmit, handleDelete }) => {
  const { currentVersion, isLoading, jsonRules, setErrors } =
    useRoutingRulesStore();
  const submitWorkflow = useSubmitWorkflow();
  const navigate = useNavigate();
  const [pagination, setPagination] = usePagination();
  const { last } = useContext(SelectedEnvironmentContext);
  const { connectors: liveConnectors } = useInventoryData({
    assessmentId: last?.doc?.data.assessmentId ?? "",
    jobId: last?.doc?.id ?? "",
    scopes: last?.doc?.data.scope ?? [],
  });

  const { managedConnectorCount, unmanagedViolators } = useMemo(() => {
    const managed = new Set<string>();
    const unmanagedSeen = new Set<string>();
    const violators: GovernanceViolator[] = [];
    for (const c of liveConnectors) {
      const name = getConnectorDisplayName(c);
      if (isConnectorManaged(c)) {
        managed.add(name);
      } else if (!unmanagedSeen.has(name)) {
        unmanagedSeen.add(name);
        violators.push({
          id: getConnectorId(c),
          name,
          platform: "Direct",
          source: "Discovered via inventory",
          discoveredAt: "this scan",
        });
      }
    }
    return {
      managedConnectorCount: managed.size,
      unmanagedViolators: violators,
    };
  }, [liveConnectors]);

  const governancePolicies = useMemo(
    () =>
      GOVERNANCE_POLICIES.map((policy) =>
        policy.id === "mcp-server-must-be-managed"
          ? {
              ...policy,
              enforcedCount: managedConnectorCount,
              violators: unmanagedViolators,
            }
          : policy
      ),
    [managedConnectorCount, unmanagedViolators]
  );
  const [drawerPolicyId, setDrawerPolicyId] = useState<string>();
  const [disablingRuleId, setDisablingRuleId] = useState<string | undefined>(
    undefined
  );
  const [searchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useEnumSearchParam(
    "type",
    isTypeFilter,
    TYPE_FILTER_DEFAULT
  );
  const resolvedType = typeFilter ?? TYPE_FILTER_DEFAULT;
  const [statusFilter, setStatusFilter] = useEnumSearchParam(
    "status",
    isStatusFilter,
    STATUS_FILTER_DEFAULT
  );
  const drawerPolicy = governancePolicies.find(
    (policy) => policy.id === drawerPolicyId
  );

  // Merge hardcoded governance policies with control rules from the store.
  // Governance rows are presentation-only — they surface posture rules in
  // the same place as runtime access controls without changing the backing
  // routing-rules document.
  const mergedRows = useMemo<PolicyRow[]>(
    () => [
      ...governancePolicies.map((policy) => ({
        id: `gov:${policy.id}`,
        kind: "governance" as const,
        policy,
      })),
      ...jsonRules.map((rule) => ({
        id: `ctrl:${hashRule(rule)}`,
        kind: "control" as const,
        rule,
      })),
    ],
    [governancePolicies, jsonRules]
  );

  const visibleRows = useMemo(
    () =>
      mergedRows.filter((row) => {
        if (row.kind !== typeFilter) return false;
        const enabled = isGovernanceRow(row)
          ? row.policy.enabled
          : !row.rule.disabled;
        if (statusFilter === "enabled" && !enabled) return false;
        if (statusFilter === "disabled" && enabled) return false;
        return true;
      }),
    [mergedRows, typeFilter, statusFilter]
  );

  const handleTypeFilter = useCallback(
    (value: number | string) => {
      const v = String(value);
      if (isTypeFilter(v)) setTypeFilter(v);
    },
    [setTypeFilter]
  );
  const handleStatusFilter = useCallback(
    (value: number | string) => {
      const v = String(value);
      if (isStatusFilter(v)) setStatusFilter(v);
    },
    [setStatusFilter]
  );

  const rowClassName = useCallback((record: PolicyRow) => {
    if (isGovernanceRow(record)) return "governance-row";
    const disabled = !!record.rule.disabled;
    return `control-row${disabled ? " disabled-rule" : ""}`;
  }, []);

  // Table filters
  const uniqueResources = useMemo(
    () => getUniqueResources(jsonRules),
    [jsonRules]
  );
  const resourceFilters = useMemo(
    () => getResourceFilters(uniqueResources),
    [uniqueResources]
  );
  const disabledFilters = useMemo(
    () => [
      { text: "Enabled", value: false },
      { text: "Disabled", value: true },
    ],
    []
  );

  const updatePagination = useCallback(
    (page: number, pageSize: number) => setPagination({ page, pageSize }),
    [setPagination]
  );

  // Deep-link create: if a rule is provided via the `yaml` query param, open
  // the create page seeded with it.
  useEffect(() => {
    const yamlStr = searchParams.get("yaml");
    if (!yamlStr) return;
    setErrors([]);
    navigate(`new?yaml=${encodeURIComponent(yamlStr)}`);
  }, [searchParams, setErrors, navigate]);

  const handleEditRule = useCallback(
    (rule: RoutingRule) => {
      if (!canSubmit) return;
      navigate(`edit/${hashRule(rule)}`);
    },
    [navigate, canSubmit]
  );

  const handleRowClick = useCallback(
    (record: PolicyRow) => {
      if (isGovernanceRow(record)) {
        setDrawerPolicyId(record.policy.id);
        return;
      }
      handleEditRule(record.rule);
    },
    [handleEditRule]
  );

  const closeDrawer = useCallback(() => setDrawerPolicyId(undefined), []);

  const setRuleDisabled = useCallback(
    async (rule: RoutingRule, ruleId: string, disabled: boolean) => {
      setDisablingRuleId(ruleId);

      const updatedRules = [...jsonRules];
      const newRule = { ...rule, disabled };

      const existingRuleHash = stringify(rule);
      const index = updatedRules.findIndex(
        (r) => stringify(r) === existingRuleHash
      );
      if (index !== -1) {
        updatedRules[index] = newRule;
      } else {
        console.error("Rule not found");
        setDisablingRuleId(undefined);
        return;
      }

      const policyName = rule.name ? `Policy "${rule.name}"` : "Policy";
      const action = disabled ? "disabled" : "enabled";
      try {
        await submitWorkflow({
          jsonData: { currentVersion, workflow: { rules: updatedRules } },
          shouldNavigate: false,
          successMessage: `${policyName} was successfully ${action}.`,
        });
      } finally {
        setDisablingRuleId(undefined);
      }
    },
    [currentVersion, jsonRules, submitWorkflow]
  );

  const columns: ResizableTableColumnsType<PolicyRow> = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        width: 200,
        filterDropdown: (props: any) => (
          <FilterDropdown {...props} placeholder="Search..." />
        ),
        filterIcon: () => <SearchOutlined />,
        onFilter: (value, record) => {
          const name = isGovernanceRow(record)
            ? record.policy.name
            : (record.rule.name ?? "");
          const description = isGovernanceRow(record)
            ? record.policy.description
            : "";
          return `${name} ${description}`
            .toLowerCase()
            .includes(String(value).toLowerCase());
        },
        render: (_, record) => {
          if (isGovernanceRow(record)) {
            return (
              <>
                <Flex gap={8}>
                  <SafetyCertificateOutlined
                    style={{ color: colors.tagColors.yellow.text }}
                  />
                  <NamePreview name={record.policy.name} />
                </Flex>
                <Typography.Text
                  type="secondary"
                  style={{
                    display: "block",
                    overflow: "clip",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {record.policy.description}
                </Typography.Text>
              </>
            );
          }
          return (
            <Flex gap={8}>
              <ControlOutlined style={{ color: colors.tagColors.blue.text }} />
              <NamePreview name={record.rule.name} />
            </Flex>
          );
        },
      },
      {
        title: resolvedType === "control" ? "Identity" : "Scope",
        key: resolvedType === "control" ? "requestor" : "scope",
        width: 200,
        filterDropdown: (props: any) => (
          <FilterDropdown {...props} placeholder="Search..." />
        ),
        filterIcon: () => <SearchOutlined />,
        onFilter: (value, record) => {
          const needle = String(value).toLowerCase();
          if (isGovernanceRow(record)) {
            return record.policy.appliesToLabel.toLowerCase().includes(needle);
          }
          const yamlContent = yaml.dump(record.rule.requestor);
          return yamlContent.toLowerCase().includes(needle);
        },
        render: (_, record) => {
          if (isGovernanceRow(record)) {
            return (
              <Typography.Text type="secondary">
                {record.policy.appliesToLabel}
              </Typography.Text>
            );
          }
          return <RequestorPreview requestor={record.rule.requestor} />;
        },
      },
      ...(resolvedType === "control"
        ? [
            {
              title: "Resource",
              key: "resource",
              width: 200,
              filters: resourceFilters,
              onFilter: (value: any, record: PolicyRow) =>
                isControlRow(record) && record.rule.resource.type === value,
              render: (_: any, record: PolicyRow) => {
                if (isGovernanceRow(record)) {
                  return <Typography.Text disabled>-</Typography.Text>;
                }
                const resource: ResourceRule = record.rule.resource;
                return <ResourcePreview resource={resource} />;
              },
            },
          ]
        : []),
      {
        title: resolvedType === "control" ? "Status" : "Compliance",
        key: resolvedType === "control" ? "status" : "compliance",
        width: 500,
        render: (_, record) => {
          if (isGovernanceRow(record)) {
            return (
              <ComplianceCell
                percent={
                  getGovernancePolicyStats(record.policy).compliancePercent
                }
              />
            );
          }
          const approval: ApprovalRule[] = record.rule.approval;
          return <ApprovalSummary record={approval} />;
        },
      },
      {
        title: "Enabled",
        key: "disabled",
        width: 100,
        filters: disabledFilters,
        onFilter: (value, record) => {
          const disabled = isGovernanceRow(record)
            ? !record.policy.enabled
            : !!record.rule.disabled;
          return disabled === !!value;
        },
        render: (_, record) => {
          const disabled = isGovernanceRow(record)
            ? !record.policy.enabled
            : !!record.rule.disabled;
          const isReadOnly = isGovernanceRow(record);

          return (
            <CellAction>
              <Tooltip
                title={
                  isReadOnly
                    ? "Governance policies are read-only in this view."
                    : "Toggle access policy. Disabled policies are not evaluated as part of requests."
                }
              >
                <Switch
                  checked={!disabled}
                  // eslint-disable-next-line react/jsx-no-bind
                  onChange={(checked) => {
                    if (!isControlRow(record)) return;
                    setRuleDisabled(record.rule, record.id, !checked);
                  }}
                  disabled={!isReadOnly && disablingRuleId !== undefined}
                  loading={disablingRuleId === record.id}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                />
              </Tooltip>
            </CellAction>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: 80,
        render: (_, record) => {
          return canSubmit ? (
            <CellAction>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                // Memoized by antd Table
                // eslint-disable-next-line react/jsx-no-bind
                onClick={
                  isControlRow(record)
                    ? () => {
                        confirm({
                          content: "This action cannot be undone.",
                          okText: "Yes, delete policy",
                          okType: "danger",
                          onOk() {
                            handleDelete(record.rule);
                          },
                          title: "Are you sure you want to delete this policy?",
                        });
                      }
                    : undefined
                }
              />
            </CellAction>
          ) : null;
        },
      },
    ],
    [
      canSubmit,
      disabledFilters,
      disablingRuleId,
      handleDelete,
      resolvedType,
      resourceFilters,
      setRuleDisabled,
    ]
  );

  const rowKey = useCallback((record: PolicyRow) => nonNullable(record.id), []);

  const onRow = useCallback(
    (record: PolicyRow) => ({ onClick: () => handleRowClick(record) }),
    [handleRowClick]
  );

  return (
    <StyledTableContainer $canSubmit={canSubmit}>
      <StyledToolbar>
        <Segmented
          options={TYPE_FILTER_OPTIONS}
          value={typeFilter}
          onChange={handleTypeFilter}
          size="small"
        />
        <Segmented
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={handleStatusFilter}
          size="small"
        />
      </StyledToolbar>
      <ResizableTable
        tableId="policies"
        columns={columns}
        dataSource={visibleRows}
        loading={isLoading}
        rowKey={rowKey}
        onRow={onRow}
        pagination={{
          current: pagination.page,
          defaultCurrent: 1,
          pageSize: pagination.pageSize,
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: updatePagination,
        }}
        rowClassName={rowClassName}
      />
      <GovernanceDetailDrawer policy={drawerPolicy} onClose={closeDrawer} />
    </StyledTableContainer>
  );
};

export default TableView;
